import type { PlaybookRunFragment } from "@qw/graphql";

/**
 * Statuses where the execution runtime is driving the run itself. `awaiting_user_input` is
 * deliberately absent: it means Alby is waiting on the broker, which is the one state where the
 * composer has to stay reachable — compact mode removes the composer outright, so it can't apply
 * while a run is parked on a human response.
 *
 * This repo already settled that question once, in the other direction from this set's name:
 * `chat.helpers.ts`'s `SESSION_BUSY_STATUSES` deliberately *excludes* `AWAITING_USER_INPUT` and
 * `AWAITING_CHILD_PLAYBOOK`, on the rationale that "locking the composer in either state freezes
 * the chat for the user — they can't type free-form context alongside the elicit form's
 * Skip/Confirm/Deny buttons." Compact mode is a stronger lock than that one ever was, so it has
 * to respect the same exclusion.
 */
const ACTIVE_PLAYBOOK_RUN_STATUSES = new Set(["running", "awaiting_child_playbook"]);

/**
 * How long a run must have been active before its transcript is torn down for compact mode.
 * A run that settles inside this window never costs enough to be worth the teardown and
 * authoritative reload a compact round trip pays for — it would only bounce the surface
 * through compact and back.
 *
 * 30s is where run age starts predicting run length. Across 13,336 inbox root runs, a run
 * still going at 10s, 15s or 20s had a median ~37s left either way; one still going at 30s
 * had 66s left, and the gap widens from there. It also costs almost nothing on the runs this
 * mode exists for: broker-initiated playbooks have a 9.4 minute median, and only 2.2% of them
 * settle inside 30s, while 40% of background-triggered runs do and now never disturb the
 * surface at all.
 */
export const COMPACT_PLAYBOOK_DELAY_MS = 30_000;

export interface ActivePlaybookMode {
  activeRun: PlaybookRunFragment;
  rootRun: PlaybookRunFragment;
}

export type CompactPlaybookDisplayMode =
  | { kind: "normal" }
  | { kind: "compact"; runId: string }
  | { kind: "live"; runId: string }
  | { kind: "restoring"; runId: string };

/**
 * The single policy for which surface a chat panel shows, applied identically on the first render
 * and every render after it.
 *
 * Seeding and steady-state used to be two functions, and only the seed knew about `dismissedRunId`.
 * That split was itself the bug: on a cold Apollo cache the first render has no runs yet, so the
 * seed ran with `activeRunId === null` and the dismissal never applied, and the render after the
 * query resolved saw `normal` and unconditionally re-compacted. A broker who clicked "Show live
 * transcript" landed back in compact mode on the next reload — the refresh-proof loop this whole
 * change exists to close. One function, called with `current = { kind: "normal" }` for the initial
 * state, removes the class of bug rather than this one instance of it.
 */
export const deriveCompactPlaybookDisplayMode = (
  current: CompactPlaybookDisplayMode,
  enabled: boolean,
  activeRunId: string | null,
  dismissedRunId: string | null
): CompactPlaybookDisplayMode => {
  if (!enabled) {
    return current.kind === "normal" ? current : { kind: "normal" };
  }

  if (activeRunId) {
    // An explicit in-session choice for this run outranks the stored one.
    if ((current.kind === "compact" || current.kind === "live") && current.runId === activeRunId) {
      return current;
    }
    // `live` rather than `normal` for a dismissed run: `normal` would be re-compacted by the
    // branch below on the very next render.
    return activeRunId === dismissedRunId
      ? { kind: "live", runId: activeRunId }
      : { kind: "compact", runId: activeRunId };
  }

  if (current.kind === "compact" || current.kind === "live") {
    return { kind: "restoring", runId: current.runId };
  }

  return current;
};

export const isPlaybookRunActive = (run: Pick<PlaybookRunFragment, "status">): boolean =>
  ACTIVE_PLAYBOOK_RUN_STATUSES.has(run.status);

export const isPlaybookRunAwaitingUser = (run: Pick<PlaybookRunFragment, "status">): boolean =>
  run.status === "awaiting_user_input";

const startedAtTimestamp = (run: Pick<PlaybookRunFragment, "startedAt">): number => {
  const timestamp = new Date(String(run.startedAt)).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const resolveActivePlaybookMode = (runs: PlaybookRunFragment[]): ActivePlaybookMode | null => {
  // A tree parked on the broker is never compacted, at any depth. The direct case is a root run
  // sitting in `awaiting_user_input`; the nested case is a root in `awaiting_child_playbook` whose
  // descendant holds the elicit, which reads as perfectly in-flight from the root alone. Both are
  // protected from every server-side recovery sweep by `buildProtectedSessionIds`, so they persist
  // until a human answers — indefinitely, for the ~1.8k runs parked past a week.
  if (runs.some(isPlaybookRunAwaitingUser)) {
    return null;
  }

  const activeRun = runs.filter(isPlaybookRunActive).sort((left, right) => {
    const depthDifference = (right.depth ?? 0) - (left.depth ?? 0);
    return depthDifference || startedAtTimestamp(right) - startedAtTimestamp(left);
  })[0];

  if (!activeRun) {
    return null;
  }

  const runsById = new Map(runs.map((run) => [run.id, run]));
  let rootRun = activeRun;
  const visited = new Set<string>();

  while (rootRun.parentPlaybookRunId && !visited.has(rootRun.id)) {
    visited.add(rootRun.id);
    const parent = runsById.get(rootRun.parentPlaybookRunId);
    if (!parent) {
      break;
    }
    rootRun = parent;
  }

  return { activeRun, rootRun };
};

export const compactPlaybookDelayRemainingMs = (startedAt: unknown, now: number): number => {
  const startedAtMs = new Date(String(startedAt)).getTime();
  // An unreadable start time cannot be aged. Fall toward containment rather than streaming a
  // run of unknown age — possibly hours old — into the transcript.
  if (!Number.isFinite(startedAtMs)) {
    return 0;
  }

  return Math.max(0, startedAtMs + COMPACT_PLAYBOOK_DELAY_MS - now);
};

export const getPlaybookRunStatusLabel = (status: string): string => {
  switch (status) {
    case "awaiting_child_playbook":
      return "Waiting for a nested playbook";
    case "running":
      return "Running";
    default:
      return "In progress";
  }
};

export const getRunningToolName = (run: PlaybookRunFragment): string | null => {
  const invocation = run.toolInvocations
    .filter(({ status }) => status === "running")
    .sort((left, right) => new Date(String(right.calledAt)).getTime() - new Date(String(left.calledAt)).getTime())[0];

  return invocation?.toolName ?? null;
};

export const formatElapsedTime = (startedAt: unknown, now: number): string => {
  const startedAtMs = new Date(String(startedAt)).getTime();
  if (!Number.isFinite(startedAtMs)) {
    return "Started recently";
  }

  const elapsedSeconds = Math.max(0, Math.floor((now - startedAtMs) / 1000));
  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  if (hours > 0) {
    return `Running for ${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `Running for ${minutes}m ${seconds}s`;
  }
  return `Running for ${seconds}s`;
};
